// @ts-nocheck
/**
 * Store Locator Helper Utilities
 * Pure functions for distance calculation, hours formatting, and API helpers
 */

/**
 * Uberall API endpoint for fetching ALL locations
 * @param {string} storeKey - The Uberall store finder key
 * @returns {string} The API endpoint URL
 */
export const getApiEndpoint = (storeKey) =>
  `https://uberall.com/api/store-finders/${storeKey}/locations/all`;

/**
 * Build fieldMask query string from array of fields
 * @param {string[]} fields - Array of field names to request
 * @returns {string} Query string with fieldMask parameters
 */
export const buildFieldMask = (fields) => '?' + fields.map((f) => `fieldMask=${f}`).join('&');

/** Default fields for store list view. */
export const STORE_LIST_FIELDS = [
  'city',
  'country',
  'id',
  'lat',
  'lng',
  'name',
  'nextOpen',
  'openingHours',
  'openNow',
  'phone',
  'photos',
  'province',
  'streetAndNumber',
  'zip',
  'shopify_store_page_url',
  'About_store',
  'futureOpeningDate',
  'customFields',
];

/** Fields for store detail view (includes additional fields). */
export const STORE_DETAIL_FIELDS = [
  'city',
  'country',
  'email',
  'id',
  'lat',
  'lng',
  'name',
  'nextOpen',
  'openingHours',
  'openNow',
  'phone',
  'photos',
  'province',
  'services',
  'streetAndNumber',
  'zip',
  'shopify_store_page_url',
  'futureOpeningDate',
  'About_store',
  'events_custom',
  'Blog_one',
  'Blog_two',
  'Blog_one_cta_copy',
  'Blog_two_cta_copy',
  'customFields',
];

/**
 * Get endpoint for a specific store by ID
 * @param {string} storeKey - The Uberall store finder key
 * @param {string|number} storeId - The store location ID
 * @returns {string} The API endpoint URL for the specific store
 */
export const getStoreDetailEndpoint = (storeKey, storeId) =>
  `https://uberall.com/api/store-finders/${storeKey}/locations/${storeId}`;

/**
 * Fetch a single store's details from Uberall API
 * @param {string} storeKey - The Uberall store finder key
 * @param {string|number} storeId - The store location ID
 * @param {AbortSignal} [signal] - Optional AbortController signal
 * @returns {Promise<Object|null>} Store data or null on error
 */
export async function fetchStoreDetails(storeKey, storeId, signal) {
  const endpoint = getStoreDetailEndpoint(storeKey, storeId);
  const fieldMask = buildFieldMask(STORE_DETAIL_FIELDS);

  try {
    const response = await fetch(`${endpoint}${fieldMask}`, { signal });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();

    // API returns data directly in response, not under response.location
    return data.response || null;
  } catch (error) {
    if (error.name === 'AbortError') return null;
    return null;
  }
}

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param {number} lat1 - Latitude of first point
 * @param {number} lng1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lng2 - Longitude of second point
 * @returns {number} Distance in miles, rounded to 1 decimal
 */
export function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 3959; // Earth's radius in miles
  const toRad = (deg) => deg * (Math.PI / 180);

  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return Math.round(R * c * 10) / 10;
}

/**
 * Get the current day index for openingHours array
 * Uberall uses Monday = 0, Sunday = 6
 * JavaScript Date uses Sunday = 0, Saturday = 6
 * @returns {number} Day index for openingHours array (0-6, Monday-Sunday)
 */
function getTodayIndex() {
  const jsDay = new Date().getDay();
  // Convert: JS Sunday(0) -> 6, JS Monday(1) -> 0, etc.
  return jsDay === 0 ? 6 : jsDay - 1;
}

/**
 * Format hour from 24h to 12h format
 * @param {string} time - Time in HH:MM format
 * @returns {string} Formatted time (e.g., "9am", "5pm")
 */
function formatHour(time) {
  if (!time) return '';
  const [hours] = time.split(':');
  const hour = parseInt(hours, 10);
  if (hour === 0) return '12am';
  if (hour === 12) return '12pm';
  if (hour < 12) return `${hour}am`;
  return `${hour - 12}pm`;
}

/**
 * Get store hours status message
 * @param {Object} store - Store data from Uberall
 * @param {Object} translations - Translation strings
 * @returns {Object} Status object with text and isOpen boolean
 */
export function getStoreHoursStatus(store, translations) {
  // Check for future opening date
  if (store.futureOpeningDate) {
    const futureDate = new Date(store.futureOpeningDate);
    const now = new Date();

    if (futureDate > now) {
      const dateStr = futureDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
      return {
        text: translations.coming_soon.replace('{{ date }}', dateStr),
        isOpen: false,
        isComingSoon: true,
      };
    }
  }

  // Check for permanently/temporarily closed
  if (store.nextOpen?.neverOpens) {
    return {
      text: translations.temporarily_closed,
      isOpen: false,
      isClosed: true,
    };
  }

  // Check if currently open
  if (store.openNow) {
    const todayHours = store.openingHours?.[getTodayIndex()];
    const closingTime = todayHours?.to1;

    if (closingTime) {
      return {
        text: translations.open_until.replace('{{ time }}', formatHour(closingTime)),
        isOpen: true,
      };
    }

    return {
      text: translations.open_now,
      isOpen: true,
    };
  }

  // Store is closed - show when it opens
  const openHour = store.nextOpen?.hour;
  if (openHour) {
    return {
      text: translations.opens_at.replace('{{ time }}', formatHour(openHour)),
      isOpen: false,
    };
  }

  return {
    text: translations.closed,
    isOpen: false,
  };
}

/**
 * Format store address
 * @param {Object} store - Store data from Uberall
 * @returns {string} Formatted address string
 */
export function formatAddress(store) {
  const parts = [store.streetAndNumber, store.city, store.province, store.zip].filter(Boolean);
  return parts.join(', ');
}

/**
 * Format phone number for tel: link
 * @param {string} phone - Phone number string
 * @returns {string} Cleaned phone number for tel: href
 */
export function formatPhoneHref(phone) {
  if (!phone) return '';
  return phone.replace(/[^\d+]/g, '');
}

/**
 * Format time from 24h to 12h format
 * @param {string} time - Time in HH:MM format
 * @returns {string} Time in 12h format (e.g., "10am", "6pm")
 */
function formatTime(time) {
  if (!time) return '';
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'pm' : 'am';
  const hour12 = hours % 12 || 12;
  return minutes === 0
    ? `${hour12}${period}`
    : `${hour12}:${minutes.toString().padStart(2, '0')}${period}`;
}

/**
 * Format opening hours for display
 * Groups consecutive days with same hours
 * @param {Array} openingHours - Array of opening hours from Uberall
 * @param {Array} [dayNames] - Optional array of localized day names (index 0 empty, 1-7 are Mon-Sun)
 * @returns {Array} Array of formatted hour strings
 */
export function formatOpeningHours(openingHours, dayNames) {
  if (!openingHours || !Array.isArray(openingHours) || openingHours.length === 0) {
    return [];
  }

  const days = dayNames || [
    '',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];

  // Sort by day of week
  const sorted = [...openingHours].sort((a, b) => a.dayOfWeek - b.dayOfWeek);

  // Group consecutive days with same hours
  const groups = [];
  let currentGroup = null;

  sorted.forEach((day) => {
    const hours = `${formatTime(day.from1)} - ${formatTime(day.to1)}`;

    if (currentGroup && currentGroup.hours === hours && currentGroup.endDay === day.dayOfWeek - 1) {
      // Extend current group
      currentGroup.endDay = day.dayOfWeek;
    } else {
      // Start new group
      if (currentGroup) groups.push(currentGroup);
      currentGroup = {
        startDay: day.dayOfWeek,
        endDay: day.dayOfWeek,
        hours,
      };
    }
  });

  if (currentGroup) groups.push(currentGroup);

  // Format each group
  return groups.map((group) => {
    const startName = days[group.startDay];
    const endName = days[group.endDay];

    if (group.startDay === group.endDay) {
      return `${startName}: ${group.hours}`;
    } else {
      return `${startName}-${endName}: ${group.hours}`;
    }
  });
}

/**
 * Build Google Maps directions URL
 * @param {Object} store - Store data with lat, lng, and name
 * @returns {string} Google Maps directions URL
 */
export function getDirectionsUrl(store) {
  const destination = encodeURIComponent(`${store.name}, ${formatAddress(store)}`);
  return `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
}

/**
 * Turn a place a shopper typed into coordinates: a postal code, a city, a street address.
 *
 * The pickup drawers search a fixed list of stores, so a typed postal code can only ever
 * match the one store that happens to sit in it — "10014" names a neighbourhood with no
 * store in it and matches nothing at all. Resolving the query to a point instead lets them
 * answer the question actually being asked: what can I collect from near here.
 *
 * The Geocoding web service rather than the Maps JS API the locator's map loads: these
 * drawers need a coordinate, not a map, and this is one small fetch against the same key.
 * @param {string} query - What the shopper typed.
 * @param {string} mapsKey - The Google Maps API key.
 * @param {string} [country] - ISO country code to restrict the search to, so "Boston" means
 *   the shopper's own market's Boston.
 * @returns {Promise<{lat: number, lng: number}|null>} Coordinates, or null when the query
 *   names no place we can find.
 */
export async function geocodeQuery(query, mapsKey, country) {
  const address = String(query ?? '').trim();
  if (!address || !mapsKey) return null;

  const url = new URL('https://maps.googleapis.com/maps/api/geocode/json');
  url.searchParams.set('address', address);
  url.searchParams.set('key', mapsKey);
  if (country) url.searchParams.set('components', `country:${country}`);

  try {
    const response = await fetch(url);
    if (!response.ok) return null;

    const result = (await response.json())?.results?.[0];

    // "100" and "1001x" both resolve to the centre of the United States; a radius there is
    // worse than no answer.
    if (!result || result.types?.includes('country')) return null;

    const lat = Number(result.geometry?.location?.lat);
    const lng = Number(result.geometry?.location?.lng);

    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  } catch {
    // Offline, blocked, or over quota: the caller falls back to its text search.
    return null;
  }
}

/**
 * Sort stores by distance from user location
 * @param {Object[]} stores - Array of store objects
 * @param {Object} userLocation - User coordinates { lat, lng }
 * @returns {Object[]} Sorted stores with distance property added
 */
export function sortStoresByDistance(stores, userLocation) {
  if (!userLocation) return stores;

  return sortStoresByNearestOrigin(stores, [userLocation]);
}

/**
 * Sort stores by distance to the nearest of several origins. Several origins are the stores
 * a partial postal code anchors on, so each store is measured from whichever it is near.
 * @param {Object[]} stores - Store objects carrying lat/lng
 * @param {Object[]} origins - Points to measure from, `{ lat, lng }` each
 * @returns {Object[]} Sorted stores, each with a `distance` to its nearest origin
 */
export function sortStoresByNearestOrigin(stores, origins) {
  if (!origins?.length) return stores;

  return stores
    .map((store) => ({
      ...store,
      distance: Math.min(
        ...origins.map((origin) => calculateDistance(origin.lat, origin.lng, store.lat, store.lng))
      ),
    }))
    .sort((a, b) => a.distance - b.distance);
}

/**
 * Sort stores alphabetically by cleaned name
 * @param {Object[]} stores - Array of store objects
 * @returns {Object[]} Alphabetically sorted stores
 */
export function sortStoresAlphabetically(stores) {
  return [...stores].sort((a, b) => {
    const nameA = (a.name || '')
      .replace(/^Veronica Beard\s*[-–—]?\s*/i, '')
      .trim()
      .toLowerCase();
    const nameB = (b.name || '')
      .replace(/^Veronica Beard\s*[-–—]?\s*/i, '')
      .trim()
      .toLowerCase();
    return nameA.localeCompare(nameB);
  });
}

/** Cache key for sessionStorage. */
const CACHE_KEY = 'store_locator_cache';
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

/**
 * Get cached store data. An empty list counts as no cache: `[]` is truthy, so one empty
 * response used to answer every reader for the next half hour.
 * @returns {Object[]|null} Cached locations, or null if empty, expired, missing or malformed
 */
export function getCachedStores() {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > CACHE_TTL) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }

    if (!Array.isArray(data) || data.length === 0) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }

    return data;
  } catch {
    return null;
  }
}

/**
 * Cache store data. Nothing is written for an empty list — see getCachedStores.
 * @param {Object[]} stores - Store data to cache
 */
export function setCachedStores(stores) {
  if (!Array.isArray(stores) || stores.length === 0) return;

  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        data: stores,
        timestamp: Date.now(),
      }),
    );
  } catch {
    // Ignore storage errors (quota exceeded, etc.)
  }
}

/**
 * Get a photo URL by type from a store's photos array
 * @param {Object[]} photos - Array of photo objects from Uberall API
 * @param {string} type - Photo type to find (e.g., 'MAIN', 'STOREFINDER_COVER')
 * @returns {string|null} URL of the photo or null if not found
 */
export function getPhotoByType(photos, type) {
  if (!photos || !Array.isArray(photos) || photos.length === 0) {
    return null;
  }

  const photo = photos.find((p) => p.type === type);
  return photo?.publicUrl || null;
}

/**
 * Get the main photo URL from a store's photos array
 * @param {Object[]} photos - Array of photo objects from Uberall API
 * @returns {string|null} URL of the main photo or null if not found
 */
export function getMainPhotoUrl(photos) {
  return getPhotoByType(photos, 'MAIN');
}

/**
 * Get user's current geolocation
 * @returns {Promise<{lat: number, lng: number}>} User coordinates
 */
export function getUserGeolocation() {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('Geolocation is not supported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      (error) => {
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes
      },
    );
  });
}

/**
 * Store links come out of Uberall in two shapes: an absolute primary-domain URL, or a
 * root-relative path (`/pages/our-stores-boston-...`, which is what the locations feed
 * actually returns today). Both stay on the primary market unless they are rewritten —
 * a relative path is emitted verbatim, so a shopper on /en-gb lands back on the US page.
 *
 * Assumes the stored value is unlocalized, which is how Uberall is authored; a value
 * that already carries a market subfolder would be prefixed twice.
 *
 * @param {string} url - Store page URL or path from Uberall
 * @param {string} primaryOrigin - Primary origin, e.g. 'https://veronicabeard.com'
 * @param {string} localizedOrigin - Current market origin, e.g. 'https://veronicabeard.ca/fr'
 * @returns {string} - Localized URL, or the input when it is neither a primary-domain
 *   link nor a root-relative path (third-party and protocol-relative links pass through)
 */
export function localizeStoreUrl(url, primaryOrigin, localizedOrigin) {
  if (!url || !primaryOrigin || !localizedOrigin) return url;

  if (url.startsWith('/') && !url.startsWith('//')) {
    return `${localizedOrigin}${url}`;
  }

  const primaryOriginWww = primaryOrigin.replace('https://', 'https://www.');

  for (const origin of [primaryOriginWww, primaryOrigin]) {
    if (url.startsWith(origin)) {
      return `${localizedOrigin}${url.slice(origin.length)}`;
    }
  }

  return url;
}
